import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../ui/Icon';

// Ekranowa klawiatura dotykowa kiosku (Electron + PWA). Pojawia się przy fokusie
// pola tekstowego, w designie aplikacji, z dużymi klawiszami pod palec. Wpisuje znaki
// do aktywnego <input>/<textarea> przez natywny setter + event 'input' (React widzi zmianę).
//
// Klawisze używają onPointerDown z preventDefault, żeby NIE odbierać fokusu polu
// (inaczej input traci focus i klawiatura znika). Fizyczny skaner kodów działa
// niezależnie — to tylko ręczne wpisywanie.
//
// Nad klawiszami jest pole podglądu (vkb-preview): na żywo lustruje zawartość
// aktywnego pola wraz z pozycją kursora i zaznaczeniem, więc operator widzi co
// wpisuje nawet gdy klawiatura zasłania właściwy input. Podgląd śledzi zmiany z
// klawiatury ORAZ ze skanera (nasłuch 'input'/'selectionchange' na dokumencie).

const TEXTY = new Set(['text', 'search', 'tel', 'url', 'email', 'password', 'number']);

function isEditable(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const t = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return TEXTY.has(t);
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter ? setter.call(el, value) : (el.value = value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function caret(el: HTMLInputElement | HTMLTextAreaElement): [number, number] {
  try {
    const s = el.selectionStart, e = el.selectionEnd;
    if (s != null && e != null) return [s, e];
  } catch { /* number/search bez selekcji */ }
  return [el.value.length, el.value.length];
}
function setCaret(el: HTMLInputElement | HTMLTextAreaElement, pos: number) {
  try { el.setSelectionRange(pos, pos); } catch { /* nieobsługiwane */ }
}

function fieldType(el: HTMLInputElement | HTMLTextAreaElement): string {
  return el instanceof HTMLTextAreaElement ? 'textarea' : ((el as HTMLInputElement).type || 'text').toLowerCase();
}

// Czytelna etykieta pola dla podglądu — tylko jawne źródła (aria-label /
// aria-labelledby / <label>). Placeholder zostaje na podpowiedź pustego pola,
// żeby chip etykiety nie dublował tekstu podpowiedzi.
function fieldLabel(el: HTMLInputElement | HTMLTextAreaElement): string {
  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) return aria.trim();
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy) {
    const txt = labelledBy.split(/\s+/).map((id) => document.getElementById(id)?.textContent ?? '').join(' ').trim();
    if (txt) return txt;
  }
  const labels = (el as HTMLInputElement).labels;
  if (labels && labels.length) {
    const txt = Array.from(labels).map((l) => l.textContent ?? '').join(' ').trim();
    if (txt) return txt;
  }
  return '';
}

const LETTERS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
  ['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż', '-'],
];
const SYMBOLS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['-', '_', '.', ',', '/', ':', ';', '(', ')', '#'],
  ['@', '+', '*', '?', '!', '"', "'", '%', '&', '='],
];

type PreviewState = {
  value: string;
  selStart: number;
  selEnd: number;
  type: string;
  label: string;
  placeholder: string;
};

export default function VirtualKeyboard() {
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(false);
  const [mode, setMode] = useState<'abc' | 'sym'>('abc');
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const targetRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewFieldRef = useRef<HTMLDivElement | null>(null);
  const caretRef = useRef<HTMLSpanElement | null>(null);

  // Zrzut stanu aktywnego pola do podglądu (wartość + kursor + typ + etykieta).
  const syncPreview = useCallback(() => {
    const el = targetRef.current;
    if (!el) { setPreview(null); return; }
    const [s, e] = caret(el);
    setPreview({
      value: el.value,
      selStart: s,
      selEnd: e,
      type: fieldType(el),
      label: fieldLabel(el),
      placeholder: (el as HTMLInputElement).placeholder ?? '',
    });
  }, []);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as Element;
      if (isEditable(el)) {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        targetRef.current = el;
        setOpen(true);
        syncPreview();
      }
    };
    const onFocusOut = () => {
      // Drobne opóźnienie: tap w klawisz nie powoduje focusout (preventDefault),
      // ale przejście do innego elementu / zamknięcie pola — tak.
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => {
        if (!isEditable(document.activeElement)) { setOpen(false); targetRef.current = null; setPreview(null); }
      }, 150);
    };
    // Skaner kodów / mysz / klawisze fizyczne też zmieniają wartość i kursor —
    // lustrzymy je w podglądzie przez nasłuch na dokumencie.
    const onDocInput = (e: Event) => { if (e.target === targetRef.current) syncPreview(); };
    const onSelectionChange = () => { if (targetRef.current && document.activeElement === targetRef.current) syncPreview(); };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('input', onDocInput);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('input', onDocInput);
      document.removeEventListener('selectionchange', onSelectionChange);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [syncPreview]);

  // Trzymaj kursor podglądu w widoku przy długim tekście (przewiń poziomo).
  useEffect(() => {
    const field = previewFieldRef.current;
    const mark = caretRef.current;
    if (!field || !mark) return;
    const left = mark.offsetLeft;
    const right = left + mark.offsetWidth;
    const pad = 28;
    if (right > field.scrollLeft + field.clientWidth) field.scrollLeft = right - field.clientWidth + pad;
    else if (left < field.scrollLeft) field.scrollLeft = Math.max(0, left - pad);
  }, [preview]);

  const type = useCallback((ch: string) => {
    const el = targetRef.current;
    if (!el) return;
    const [s, e] = caret(el);
    const next = el.value.slice(0, s) + ch + el.value.slice(e);
    setNativeValue(el, next);
    setCaret(el, s + ch.length);
    if (shift) setShift(false); // shift = jednorazowy (jak na telefonie)
    syncPreview();
  }, [shift, syncPreview]);

  const backspace = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const [s, e] = caret(el);
    if (s === e && s > 0) {
      setNativeValue(el, el.value.slice(0, s - 1) + el.value.slice(e));
      setCaret(el, s - 1);
    } else if (s !== e) {
      setNativeValue(el, el.value.slice(0, s) + el.value.slice(e));
      setCaret(el, s);
    }
    syncPreview();
  }, [syncPreview]);

  const enter = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const form = el.closest('form');
    if (form) {
      if ((form as any).requestSubmit) (form as any).requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } else {
      (el as HTMLElement).blur();
      setOpen(false);
    }
  }, []);

  // onPointerDown + preventDefault → pole zachowuje fokus (klawiatura nie znika).
  const press = (fn: () => void) => (e: React.PointerEvent) => { e.preventDefault(); fn(); };

  if (!open) return null;

  const rows = mode === 'abc' ? LETTERS : SYMBOLS;
  const showCh = (c: string) => (shift && /[a-ząćęłńóśźż]/.test(c) ? c.toUpperCase() : c);

  // Treść podglądu: wartość pola z migającym kursorem w miejscu karetki
  // (lub podświetlonym zaznaczeniem). Hasła maskujemy kropkami jak natywny input.
  let previewBody: React.ReactNode = null;
  if (preview) {
    const disp = preview.type === 'password' ? '•'.repeat(preview.value.length) : preview.value;
    const { selStart, selEnd } = preview;
    if (disp.length === 0) {
      previewBody = (
        <>
          <span className="vkb-preview-caret" ref={caretRef} />
          <span className="vkb-preview-ph">{preview.placeholder || preview.label || 'Wpisz…'}</span>
        </>
      );
    } else if (selStart !== selEnd) {
      previewBody = (
        <>
          <span>{disp.slice(0, selStart)}</span>
          <span className="vkb-preview-sel" ref={caretRef}>{disp.slice(selStart, selEnd)}</span>
          <span>{disp.slice(selEnd)}</span>
        </>
      );
    } else {
      previewBody = (
        <>
          <span>{disp.slice(0, selStart)}</span>
          <span className="vkb-preview-caret" ref={caretRef} />
          <span>{disp.slice(selStart)}</span>
        </>
      );
    }
  }

  return (
    <div className="vkb" role="group" aria-label="Klawiatura ekranowa">
      {preview && (
        <div className="vkb-preview" aria-hidden="true">
          {preview.label && <span className="vkb-preview-label">{preview.label}</span>}
          <div className="vkb-preview-field" ref={previewFieldRef}>{previewBody}</div>
        </div>
      )}
      <div className="vkb-rows">
        {rows.map((row, ri) => (
          <div className="vkb-row" key={ri}>
            {ri === rows.length - 1 && mode === 'abc' && (
              <button className="vkb-key vkb-mod" onPointerDown={press(() => setShift((v) => !v))} aria-pressed={shift}>
                <Icon name="arrow-big-up" size={22} />
              </button>
            )}
            {row.map((c) => (
              <button className="vkb-key" key={c} onPointerDown={press(() => type(showCh(c)))}>{showCh(c)}</button>
            ))}
            {ri === 3 && mode === 'abc' && (
              <button className="vkb-key vkb-mod" onPointerDown={press(backspace)} aria-label="Backspace"><Icon name="delete" size={24} /></button>
            )}
          </div>
        ))}
        <div className="vkb-row vkb-bottom">
          <button className="vkb-key vkb-mod" onPointerDown={press(() => setMode((m) => (m === 'abc' ? 'sym' : 'abc')))}>
            {mode === 'abc' ? '123' : 'ABC'}
          </button>
          {mode === 'sym' && <button className="vkb-key vkb-mod" onPointerDown={press(backspace)} aria-label="Backspace"><Icon name="delete" size={24} /></button>}
          <button className="vkb-key vkb-space" onPointerDown={press(() => type(' '))}>spacja</button>
          <button className="vkb-key vkb-enter" onPointerDown={press(enter)}><Icon name="corner-down-left" size={22} /> Enter</button>
          <button className="vkb-key vkb-mod" onPointerDown={press(() => { const el = targetRef.current; (el as HTMLElement | null)?.blur(); setOpen(false); })} aria-label="Zamknij klawiaturę">
            <Icon name="chevron-down" size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}
